import { defineStore } from 'pinia'
import { notificationRepository } from '@/service/repository/notificationRepository'
import store from '@/store'

export const useNotificationStore = defineStore('notification', {
	state: () => ({
		notificationsType: {
			SECOND_DRINK: 'upselling_drinks_reminder',
			KIOSK_WALK_IN: 'kiosk_walk_in',
			KIOSK_QR_SCAN: 'kiosk_qr_scan',
			CONSUMER_READY_TO_ORDER: 'customer_ready_to_order'
		},

		notificationUpsellingActions: {
			NOTHING: 'nothing',
			CART: 'cart'
		},
		// Recent muestra los últimos 10min
		// Main notifications array containing the unified response
		notifications: { history: [], pending: [] }, // Contains { history: [...], pending: [...] }

		isLoadingNotifications: false,
		isLoadingTicketNotifications: false,
		lastUpdateTime: null
	}),

	getters: {
		// Main getter
		getNotifications: (state) => state.notifications,

		// Get history and pending from the unified response
		getHistoryNotifications: (state) => state.notifications?.history || [],
		getPendingNotifications: (state) => state.notifications?.pending || [],

		// Get pending notifications filtered by type
		getPendingTicketNotifications: (state) => {
			return state.notifications?.pending?.filter((notification) => notification.type === 'ticket_request') || []
		},
		getPendingUpsellingNotifications: (state) => {
			// Get all pending upselling notifications from Pinia state
			const notifications = state.notifications?.pending?.filter((notification) => notification.type === 'upselling') || []

			// Get selected zones and range table names from Vuex store
			const selectedZones = store.getters["copilot/selectedZoneFilter"] || [];
			const rangeTableNames = store.getters["copilot/selectedRangeTableNames"];

			// If no filters are active, return all notifications
			if (selectedZones.length === 0 && rangeTableNames.size === 0) {
				return notifications;
			}

			const normalizedZones = selectedZones.map((z) => String(z).toLowerCase().trim());

			return notifications.filter((notification) => {
				const matchZone = notification.rooms?.some((room) => {
					const name = room.name?.toLowerCase().trim();
					return normalizedZones.includes(name);
				});

				let matchRange = false;
				if (rangeTableNames.size > 0 && notification.reservations) {
					matchRange = notification.reservations.some((res) => {
						return res.tables?.some((t) => rangeTableNames.has(String(t.name))) || rangeTableNames.has(String(res.table));
					});
				}

				return matchZone || matchRange;
			});
		},

		getAllPendingAccountKioskNotification: (state) => {
			// Get all pending account kiosk notifications from Pinia state
			const notifications = state.notifications?.pending?.filter((notification) => (notification.type === 'walk-in' || notification.type === 'qr-scan') && notification.status === 0) || []

			// Get selected zones and range table names from Vuex store
			const selectedZones = store.getters["copilot/selectedZoneFilter"] || [];
			const rangeTableNames = store.getters["copilot/selectedRangeTableNames"];

			// If no filters are active, return all notifications
			if (selectedZones.length === 0 && rangeTableNames.size === 0) {
				return notifications;
			}

			// Filter notifications by selected zones or ranges
			return notifications.filter((notification) => {
				const matchZone = notification.rooms?.some((room) => selectedZones.includes(room.name)) ||
					notification.reservations?.some((res) => selectedZones.includes(res.roomName));

				let matchRange = false;
				if (rangeTableNames.size > 0 && notification.reservations) {
					matchRange = notification.reservations.some((res) => {
						return res.tables?.some((t) => rangeTableNames.has(String(t.name))) || rangeTableNames.has(String(res.table));
					});
				}

				return matchZone || matchRange;
			});
		},

		getAllCustomerReadyToOrderNotifications: (state) => {
			// Get all pending account kiosk notifications from Pinia state
			const notifications = state.notifications?.pending?.filter((notification) => notification.status === 0 && notification.type === 'customer_ready_to_order') || []

			// Get selected zones and range table names from Vuex store
			const selectedZones = store.getters["copilot/selectedZoneFilter"] || [];
			const rangeTableNames = store.getters["copilot/selectedRangeTableNames"];

			// If no filters are active, return all notifications
			if (selectedZones.length === 0 && rangeTableNames.size === 0) {
				return notifications;
			}

			// Filter notifications by selected zones or ranges
			return notifications.filter((notification) => {
				const matchZone = notification.rooms?.some((room) => selectedZones.includes(room.name)) ||
					notification.reservations?.some((res) => selectedZones.includes(res.roomName));

				let matchRange = false;
				if (rangeTableNames.size > 0 && notification.reservations) {
					matchRange = notification.reservations.some((res) => {
						return res.tables?.some((t) => rangeTableNames.has(String(t.name))) || rangeTableNames.has(String(res.table));
					});
				}

				return matchZone || matchRange;
			});
		},

		getNotificationsForType: (state, typeArray) => {
			const zoneFilterActive = store.getters['copilot/zoneFilter']

			const notifications = state.notifications?.pending?.filter((notification) => notification.type === typeArray) || []

			if (!zoneFilterActive) {
				return notifications
			}

			// Get selected zones from Vuex store
			const selectedZones = store.getters['copilot/selectedZoneFilter'] || []

			// If no zones are selected, return all notifications
			if (selectedZones.length === 0) {
				return notifications
			}

			// Filter notifications by selected zones
			return notifications.filter((notification) => {
				// Check if notification has rooms and if any room name matches selected zones
				if (notification.rooms && Array.isArray(notification.rooms)) {
					return notification.rooms.some((room) => selectedZones.includes(room.name))
				}
				return false // If no rooms, exclude the notification
			})
		},


		// Legacy getter for backward compatibility
		getTicketNotifications: (state) => {
			return state.notifications?.pending?.filter((notification) => notification.type === 'ticket_request') || []
		},

		getAllNotificationUpsellingDrinksReminder: (state) => {
			const notifications =
				state.notifications?.pending?.filter(
					(n) => n.type === "upselling_drinks_reminder" && n.status === 0
				) || [];

			const selectedZones = store.getters["copilot/selectedZoneFilter"] || [];
			const selectedRangeIds = store.getters["copilot/selectedRangeFilter"] || [];

			if (selectedZones.length === 0 && selectedRangeIds.length === 0) {
				return notifications;
			}

			const normalizedZones = selectedZones.map((z) => String(z).toLowerCase().trim());
			const allowedTables = new Set();
			const serviceRanges = store.getters["copilot/serviceRanges"] || [];

			if (selectedRangeIds.length > 0) {
				for (const id of selectedRangeIds.map(String)) {
					const range = serviceRanges.find((r) => String(r.id) === id);
					if (!range) continue;

					if (Array.isArray(range.tables)) {
						range.tables.forEach((t) => allowedTables.add(String(t?.name ?? t).trim()));
					}

					if (range.from != null && range.to != null) {
						const a = Number(range.from);
						const b = Number(range.to);
						if (!Number.isNaN(a) && !Number.isNaN(b)) {
							const min = Math.min(a, b);
							const max = Math.max(a, b);
							for (let i = min; i <= max; i++) allowedTables.add(String(i));
						}
					}
				}
			}

			return notifications.filter((n) => {
				// 1) Zone Match
				const matchZone =
					Array.isArray(n.rooms) &&
					n.rooms.some((r) => {
						const name = r.name?.toLowerCase().trim();
						return normalizedZones.includes(name);
					});

				// 2) Range Match
				let matchRange = false;
				if (allowedTables.size > 0) {
					const candidates = new Set();
					if (n.tableNumber != null) candidates.add(String(n.tableNumber).trim());
					if (Array.isArray(n.rooms)) {
						n.rooms.forEach((room) => {
							if (Array.isArray(room.tables)) {
								room.tables.forEach((t) => candidates.add(String(t?.name ?? t).trim()));
							}
						});
					}
					for (const t of candidates) {
						if (allowedTables.has(t)) {
							matchRange = true;
							break;
						}
					}
				}

				return matchZone || matchRange;
			});
		},

		getAllReservationsNotificationUpsellingDrinksReminder: (state) => {
			const notifications = state.notifications?.pending?.filter((notification) => notification.type === 'upselling_drinks_reminder' && notification.status === 0) || []

			// Get selected zones from Vuex store
			const selectedZones = store.getters['copilot/selectedZoneFilter'] || []

			// Filter notifications by selected zones
			let filteredNotifications = notifications
			if (selectedZones.length > 0) {
				filteredNotifications = notifications.filter((notification) => {
					// Check if notification has rooms and if any room name matches selected zones
					if (notification.rooms && Array.isArray(notification.rooms)) {
						return notification.rooms.some((room) => selectedZones.includes(room.name))
					}
					return false // If no rooms, exclude the notification
				})
			}

			// Get copilot reservations from Vuex store
			const copilotReservations = store.getters['copilot/reservations'] || []

			return filteredNotifications
				.map((notification) => {
					const reservationWid = notification.reservations?.[0]?.wid
					const reservation = copilotReservations.find((r) => r.id === reservationWid)

					return {
						...reservation,
						notificationId: notification.id
					}
				})
				.filter((r) => r != null)
		},

		// Get drinks for a specific reservation (migrated from Vuex)
		getAllDrinksForReservation: () => (reservation) => {
			if (reservation && reservation.cartSession) {
				const productOrders = reservation.cartSession?.productOrders || []
				return productOrders.flatMap((order) => order.items || []).filter((item) => {
					if (item.type) {
						return item.type.toLowerCase() === 'bebida'
					}
				})
			}

			return []
		},

		// Loading states
		getIsLoadingNotifications: (state) => state.isLoadingNotifications,
		getIsLoadingTicketNotifications: (state) => state.isLoadingTicketNotifications,
		getLastUpdateTime: (state) => state.lastUpdateTime,

		// Zone filtering
		getSelectedZoneFilter: () => {
			return store.getters['copilot/selectedZoneFilter'] || []
		}
	},

	actions: {
		// Main action for the unified notifications endpoint
		async loadNotifications(date = null) {
			try {
				this.isLoadingNotifications = true
				// Assign the entire response to notifications array
				this.notifications = await notificationRepository.getNotifications(date)
				this.lastUpdateTime = new Date()
			} catch (error) {
				console.error('Error loading notifications:', error)
			} finally {
				this.isLoadingNotifications = false
			}
		},

		// Legacy actions for backward compatibility (kept for existing code)
		async loadRecentNotifications() {
			try {
				this.isLoadingNotifications = true
				const notifications = await notificationRepository.getRecentNotifications()
				this.notifications = { history: notifications, pending: [] }
				this.lastUpdateTime = new Date()
			} catch (error) {
				console.error('Error loading recent notifications:', error)
			} finally {
				this.isLoadingNotifications = false
			}
		},

		async loadTicketRequestNotifications() {
			try {
				this.isLoadingTicketNotifications = true
				const ticketNotifications = await notificationRepository.getTicketRequestNotifications()
				this.notifications = { history: [], pending: ticketNotifications }
				this.lastUpdateTime = new Date()
			} catch (error) {
				console.error('Error loading ticket request notifications:', error)
			} finally {
				this.isLoadingTicketNotifications = false
			}
		},

		async loadAllNotifications() {
			// Use the new unified endpoint instead of separate calls
			await this.loadNotifications()
		},

		// Add a new notification (for real-time updates)
		addNotification(notification) {
			// Add to history notifications
			if (!this.notifications.history) {
				this.notifications.history = []
			}
			this.notifications.history.unshift(notification)
		},

		// Update notification status (e.g., when acknowledged)
		updateNotification({ id, updates }) {
			// Update in history notifications
			if (this.notifications.history) {
				const index = this.notifications.history.findIndex((n) => n.id === id)
				if (index !== -1) {
					this.notifications.history[index] = { ...this.notifications.history[index], ...updates }
				}
			}
			// Update in pending notifications
			if (this.notifications.pending) {
				const index = this.notifications.pending.findIndex((n) => n.id === id)
				if (index !== -1) {
					this.notifications.pending[index] = { ...this.notifications.pending[index], ...updates }
				}
			}
		},

		// Remove notification (e.g., when dismissed)
		removeNotification(id) {
			// Remove from history notifications
			if (this.notifications.history) {
				this.notifications.history = this.notifications.history.filter((n) => n.id !== id)
			}
			// Remove from pending notifications
			if (this.notifications.pending) {
				this.notifications.pending = this.notifications.pending.filter((n) => n.id !== id)
			}
		},

		// Clear all notifications
		clearNotifications() {
			this.notifications = { history: [], pending: [] }
		},

		// Legacy action for backward compatibility
		async acknowledgeNotifications(notificationId, notificationData) {
			try {
				const response = await notificationRepository.acknowledgeNotification(notificationId, notificationData)

				if (response.ok) {
					// Remove the acknowledged notification
					this.removeNotification(notificationId)
					return response
				} else {
					return response
				}
			} catch (error) {
				console.error('Error acknowledging notification:', error)
				throw error
			}
		}
	}
})
